import type {
	Column,
	ColumnSizingState,
	RowData,
	Table,
	TableFeature,
} from '@tanstack/react-table';

interface ColumnPaddingColumn {
	getPaddingLeft: () => number;
	getPaddingRight: () => number;

	/**
	 * The original size of the column, without padding.
	 */
	getContentSize: () => number;
}

declare module '@tanstack/react-table' {
	// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface, unused-imports/no-unused-vars
	interface Column<TData extends RowData, TValue> extends ColumnPaddingColumn {}
}

/**
 * Adds a variable padding to columns' sizes.
 *
 * React table is designed to have the size of a column be its total size,
 * including padding. However, we want to set the size of the columns' content
 * and have a variable amount of padding depending on where a column is in the
 * table.
 */
export const ColumnPadding: TableFeature = {
	createColumn: <TData extends RowData, TValue>(
		column: Column<TData, TValue>,
		_table: Table<TData>
	): void => {
		column.getPaddingLeft = () =>
			column.getIsFirstColumn() || column.getIsFirstColumn('center')
				? 16
				: column.columnDef.meta?.thinStyle
					? 4
					: 8;

		column.getPaddingRight = () => (column.columnDef.meta?.thinStyle ? 4 : 8);

		column.getContentSize = column.getSize;
		column.getSize = () =>
			column.getContentSize() +
			column.getPaddingLeft() +
			column.getPaddingRight();
	},

	createTable: <TData extends RowData>(table: Table<TData>): void => {
		const addPadding = (columnSizes: ColumnSizingState): ColumnSizingState =>
			Object.fromEntries(
				Object.entries(columnSizes).map(([key, value]) => {
					const column = table.getColumn(key);
					return column
						? [key, value + column.getPaddingLeft() + column.getPaddingRight()]
						: [key, value];
				})
			);

		const removePadding = (columnSizes: ColumnSizingState): ColumnSizingState =>
			Object.fromEntries(
				Object.entries(columnSizes).map(([key, value]) => {
					const column = table.getColumn(key);
					return column
						? [key, value - column.getPaddingLeft() - column.getPaddingRight()]
						: [key, value];
				})
			);

		const originalSetColumnSizing = table.setColumnSizing;
		table.setColumnSizing = (updater) =>
			originalSetColumnSizing(
				typeof updater === 'function'
					? (columnSizes) => removePadding(updater(addPadding(columnSizes)))
					: removePadding(updater)
			);
	},
};
